import type React from 'react';
import { useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { openNotification } from 'components/notification';

type ChipInsertingEditor = Pick<Editor, 'chain' | 'getJSON'>;
import { RowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import {
  selectAllVariables,
  selectFlatVariableNodes,
  selectVariablesByName,
  selectVariablesByTemplate,
} from 'components/editor/grid/reduxStore/allVariableSlice';
import { selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { ContentType } from 'utils/getContentType';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';
import { useTableManipulation } from '../useTableManipulation';
import { useTableContentUndoRedo } from 'components/editor/hooks/useTableContentUndoRedo';
import { useTableTotals } from '../useTableTotals';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import { useCrmPricingGroupRowFlag } from 'hooks/useCrmPricingGroupRowFlag';
import { deriveGroupRows } from '../tableUtils/deriveGroupRows';
import type { CrmArrayLeaf, CrmPasteField } from './crmArrayPayload';
import { buildPasteFields, collectDeepLeaves, parseArrayPayload } from './crmArrayPayload';
import { rowCrmArrayConflict } from './crmArrayConflict';
import { chipNode } from './crmChipDocs';
import type { CrmInstanceResolver } from './deriveCrmRows';
import { buildAuthoredRowPatch, deriveCrmRows } from './deriveCrmRows';
import type { TableType } from 'components/editor/grid/reduxStore/table.types';
import type { VariableEntity, VariableNode } from 'services/entities/VariablesEntity';

// eslint-disable-next-line @typescript-eslint/no-empty-function -- inert handler returned when the CRM pricing embed flag is off
const NOOP_HANDLER = (_e: React.ClipboardEvent): void => {};

type UseCrmArrayVariablePasteParams = {
  rowId: string;
  field: string;
  sectionId: string;
  rowType?: string;
  getBlockId: () => string | null;
  editor?: ChipInsertingEditor | null;
  cancelPendingCellCommit?: () => void;
};

type ApplyCrmArrayFieldsParams = {
  fields: CrmPasteField[];
  grouped: boolean;
  rowId: string;
  field: string;
  editor: ChipInsertingEditor;
  blockId: string;
  sectionId: string;
  tableData: TableType;
  variablesRoot: VariableNode;
  isGroupRowEnabled: boolean;
  variablesByName: Record<string, VariableNode>;
  variablesByTemplate: Record<string, VariableNode>;
  getFieldInstances: (name: string) => CrmArrayLeaf[] | undefined;
  getTableTotals: ReturnType<typeof useTableTotals>;
  handleTableContentChanged: ReturnType<typeof useTableContentUndoRedo>['handleTableContentChanged'];
  cancelPendingCellCommit?: () => void;
  isTemplate: boolean;
  insertRange?: { from: number; to: number };
  needsSpace?: boolean;
};

export function applyCrmArrayFields({
  fields,
  grouped,
  rowId,
  field,
  editor,
  blockId,
  sectionId,
  tableData,
  variablesRoot,
  isGroupRowEnabled,
  variablesByName,
  variablesByTemplate,
  getFieldInstances,
  getTableTotals,
  handleTableContentChanged,
  cancelPendingCellCommit,
  isTemplate,
  insertRange,
  needsSpace,
}: ApplyCrmArrayFieldsParams): void {
  const [firstField] = fields;
  if (!firstField) return;

  const contentToInsert = [chipNode(firstField.chipLeaf)];
  if (needsSpace) {
    contentToInsert.push({ type: 'text', text: ' ' });
  }

  if (insertRange) {
    editor.chain().focus().insertContentAt(insertRange, contentToInsert).run();
  } else {
    editor.chain().focus().insertContent(contentToInsert).run();
  }
  cancelPendingCellCommit?.();

  // buildAuthoredRowPatch merges the chip into the target field — no pre-merge
  // here, or the chip lands in the stored value twice.
  const targetRow = tableData.rows.find((row) => row.id === rowId);
  const patch = buildAuthoredRowPatch({ row: targetRow, columns: tableData.columns, field, fields, grouped });

  // `field` already received the real insert above (at the cursor) — save that result verbatim
  // instead of buildAuthoredRowPatch's own re-derivation, which has no concept of cursor
  // position and always appends to the end.
  patch[field] = JSON.stringify(editor.getJSON());

  const rowsForDerive = tableData.rows.map((row) => (row.id === rowId ? { ...row, ...patch } : row));

  // Build resolver: payload instances first, then store
  const payloadInstancesByTemplate = new Map(fields.map((f) => [f.template, f.instances]));
  const resolve: CrmInstanceResolver = (fieldRef) => {
    const fromPayloadTemplate = payloadInstancesByTemplate.get(fieldRef.name);
    if (fromPayloadTemplate && fromPayloadTemplate.length > 0) return fromPayloadTemplate;
    const fromStore = getFieldInstances(fieldRef.name);
    if (fromStore) return fromStore;
    const node = (fieldRef.template ? variablesByTemplate[fieldRef.template] : undefined) ?? variablesByName[fieldRef.name];
    return node?.objectId ? [node as CrmArrayLeaf] : undefined;
  };

  const flatRows = deriveCrmRows(rowsForDerive, tableData.columns, resolve, isTemplate);
  const derivedRows = deriveGroupRows({
    rows: flatRows,
    columns: tableData.columns,
    variablesRoot,
    enabled: isGroupRowEnabled && !isTemplate,
  });
  const { rows, totalRows, metadata } = getTableTotals(derivedRows, tableData.columns, tableData);

  handleTableContentChanged(blockId, sectionId, tableData, { ...tableData, rows, totalRows, metadata });
}

export type ResolvedArrayVariable = {
  node: VariableNode;
  fields: CrmPasteField[];
  grouped: boolean;
  getFieldInstances: (name: string) => CrmArrayLeaf[] | undefined;
};

// Resolves a picker-clicked variable to its array instances, or shows the "no records"
// warning and returns undefined when there's nothing to generate.
export function resolveArrayFieldsForVariable({
  variable,
  flatVariableNodes,
  variablesByTemplate,
  t,
}: {
  variable: VariableEntity | VariableNode;
  flatVariableNodes: VariableNode[];
  variablesByTemplate: Record<string, VariableNode>;
  t: (key: string) => string;
}): ResolvedArrayVariable | undefined {
  const findArrayNode = (name: string): VariableNode | undefined =>
    flatVariableNodes.find((n) => n.name === name && n.shape?.type === 'array');

  const hasOwnInstances = 'variables' in variable && (variable.variables?.length ?? 0) > 0;
  const node = hasOwnInstances ? (variable as VariableNode) : findArrayNode(variable.name);
  if (!node || node.shape?.type !== 'array') return undefined;
  if (!node.variables?.length) {
    openNotification({
      type: 'warning',
      title: t('editor.variables.crm_empty_state.array_empty_title'),
      description: t('editor.variables.crm_empty_state.array_empty_message'),
    });
    return undefined;
  }

  const getFieldInstances = (name: string): CrmArrayLeaf[] | undefined => {
    if (name === node.name && node.variables?.length) return node.variables as CrmArrayLeaf[];
    const instanceNode = (node.template ? variablesByTemplate[node.template] : undefined) ?? findArrayNode(name);
    return instanceNode?.variables?.length ? (instanceNode.variables as CrmArrayLeaf[]) : undefined;
  };

  const grouped = node.shape?.grouped === true;
  const fields = buildPasteFields(node as CrmArrayLeaf, { grouped, isKnownField: (name) => Boolean(getFieldInstances(name)) });
  if (fields.length === 0) return undefined;

  return { node, fields, grouped, getFieldInstances };
}

export function useCrmArrayVariablePaste({
  rowId,
  field,
  sectionId,
  rowType,
  getBlockId,
  editor,
  cancelPendingCellCommit,
}: UseCrmArrayVariablePasteParams): (e: React.ClipboardEvent) => void {
  const isCrmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const { getTableData } = useTableManipulation();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const getTableTotals = useTableTotals();
  const variablesRoot = useAppSelector(selectAllVariables);
  const isGroupRowEnabled = useCrmPricingGroupRowFlag();
  const variablesByName = useAppSelector(selectVariablesByName);
  const variablesByTemplate = useAppSelector(selectVariablesByTemplate);
  const contentType = useAppSelector(selectContentType);
  const isTemplate = contentType === ContentType.TEMPLATE;

  const handlePaste = (e: React.ClipboardEvent): void => {
    const payload = parseArrayPayload(e.clipboardData?.getData('text/plain') ?? '');
    if (!payload) return;
    if (rowType === RowTypesIdentifier.FOOTER || rowType === RowTypesIdentifier.HEADER) return;

    const blockId = getBlockId();
    if (!blockId) return;

    const tableData = getTableData(blockId, sectionId);
    if (!tableData) return;

    const targetRow = tableData.rows.find((row) => row.id === rowId);

    // Generated rows are locked — only the authored pattern row accepts a paste.
    if (isGeneratedRow(targetRow)) return;

    if (!editor) return;

    if (rowCrmArrayConflict(targetRow, payload.template)) {
      e.preventDefault();
      e.stopPropagation();
      editor
        .chain()
        .insertVariable({
          name: payload.name,
          friendlyName: payload.friendlyName,
          path: payload.path,
          template: payload.template,
          isConflict: true,
        })
        .run();
      return;
    }

    const allLeaves = collectDeepLeaves(payload);
    if (allLeaves.length === 0) return;

    e.preventDefault();
    e.stopPropagation();

    const getFieldInstances = (name: string): CrmArrayLeaf[] | undefined => {
      const node = (payload.template ? variablesByTemplate[payload.template] : undefined) ?? variablesByName[name];
      return node?.shape?.type === 'array' && node.variables?.length ? (node.variables as CrmArrayLeaf[]) : undefined;
    };

    const grouped = payload.shape?.grouped === true;
    const fields = buildPasteFields(payload, { grouped, isKnownField: (name) => Boolean(getFieldInstances(name)) });

    applyCrmArrayFields({
      fields,
      grouped,
      rowId,
      field,
      editor,
      blockId,
      sectionId,
      tableData,
      variablesRoot,
      isGroupRowEnabled,
      variablesByName,
      variablesByTemplate,
      getFieldInstances,
      getTableTotals,
      handleTableContentChanged,
      cancelPendingCellCommit,
      isTemplate,
    });
  };

  return isCrmPricingEmbedEnabled ? handlePaste : NOOP_HANDLER;
}

type UseCrmArrayVariableSelectParams = {
  rowId: string;
  field: string;
  sectionId: string;
  rowType?: string;
  getBlockId: () => string | null;
  editor?: ChipInsertingEditor | null;
};

export function useCrmArrayVariableSelect({
  rowId,
  field,
  sectionId,
  rowType,
  getBlockId,
  editor,
}: UseCrmArrayVariableSelectParams): (
  variable: VariableEntity | VariableNode,
  range?: { from: number; to: number },
  needsSpace?: boolean
) => void {
  const { t } = useTranslation();
  const isCrmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const { getTableData } = useTableManipulation();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const getTableTotals = useTableTotals();
  const variablesRoot = useAppSelector(selectAllVariables);
  const isGroupRowEnabled = useCrmPricingGroupRowFlag();
  const variablesByName = useAppSelector(selectVariablesByName);
  const variablesByTemplate = useAppSelector(selectVariablesByTemplate);
  const flatVariableNodes = useAppSelector(selectFlatVariableNodes);
  const contentType = useAppSelector(selectContentType);
  const isTemplate = contentType === ContentType.TEMPLATE;

  return useCallback(
    (variable: VariableEntity | VariableNode, range?: { from: number; to: number }, needsSpace?: boolean) => {
      if (!isCrmPricingEmbedEnabled || !editor) return;
      if (rowType === RowTypesIdentifier.FOOTER || rowType === RowTypesIdentifier.HEADER) return;

      const blockId = getBlockId();
      if (!blockId) return;

      const tableData = getTableData(blockId, sectionId);
      if (!tableData) return;

      const targetRow = tableData.rows.find((row) => row.id === rowId);

      // Generated rows are locked — only the authored pattern row accepts an insert.
      if (isGeneratedRow(targetRow)) return;

      if (rowCrmArrayConflict(targetRow, variable.template)) {
        editor
          .chain()
          .insertVariable(
            {
              name: variable.name,
              friendlyName: variable.friendlyName,
              path: variable.path,
              template: variable.template,
              isConflict: true,
            },
            range
          )
          .run();
        return;
      }

      const resolved = resolveArrayFieldsForVariable({ variable, flatVariableNodes, variablesByTemplate, t });
      if (!resolved) return;
      const { fields, grouped, getFieldInstances } = resolved;

      applyCrmArrayFields({
        fields,
        grouped,
        rowId,
        field,
        editor,
        blockId,
        sectionId,
        tableData,
        variablesRoot,
        isGroupRowEnabled,
        variablesByName,
        variablesByTemplate,
        getFieldInstances,
        getTableTotals,
        handleTableContentChanged,
        isTemplate,
        insertRange: range,
        needsSpace,
      });
    },
    [
      isCrmPricingEmbedEnabled,
      editor,
      rowType,
      rowId,
      field,
      sectionId,
      getBlockId,
      getTableData,
      variablesRoot,
      isGroupRowEnabled,
      variablesByName,
      variablesByTemplate,
      flatVariableNodes,
      isTemplate,
      getTableTotals,
      handleTableContentChanged,
      t,
    ]
  );
}
